import { Position } from 'reactflow';
import { VERTICAL_ORIENTATION } from '../../../../utilities/constants/editorConstants';
import {
  THREE_WINDING_BASE_ALIAS,
  SWITCH_TRANSFER_BASE_ALIAS,
  resolveThreeWindingHandleFraction,
  resolveSwitchTransferHandleFraction
} from '../customNode/scaleAwareSymbolUtils';

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

const parseOffsetPx = (value, size) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return clamp(value, 0, Math.max(0, size));
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed.length) return null;
  if (trimmed.endsWith('%')) {
    const pct = parseFloat(trimmed.slice(0, -1));
    if (!isNaN(pct) && Number.isFinite(size)) {
      return clamp((pct / 100) * size, 0, Math.max(0, size));
    }
  } else if (trimmed.endsWith('px')) {
    const px = parseFloat(trimmed.slice(0, -2));
    if (!isNaN(px)) return clamp(px, 0, Math.max(0, size));
  } else {
    const px = parseFloat(trimmed);
    if (!isNaN(px)) return clamp(px, 0, Math.max(0, size));
  }
  return null;
};

const resolveGridAlignOffset = ({ gridAlign, indexFallback, size, origin, spacing }) => {
  if (!gridAlign) return null;
  const count = Math.max(1, Number(gridAlign.count) || 1);
  const rawIndex = Number(gridAlign.index);
  const idx = Number.isFinite(rawIndex)
    ? clamp(rawIndex, 0, count - 1)
    : clamp(indexFallback, 0, count - 1);
  const fraction = count <= 1 ? 0.5 : idx / (count - 1);
  if (!Number.isFinite(size) || size <= 0) {
    return null;
  }
  const snapStep = Math.max(5, Math.min(200, Number(spacing) || 25));
  const targetAbs = origin + (fraction * size);
  const snappedAbs = Math.round(targetAbs / snapStep) * snapStep;
  const offset = snappedAbs - origin;
  if (!Number.isFinite(offset)) {
    return clamp(fraction * size, 0, size);
  }
  return clamp(offset, 0, size);
};

export const computeCustomHandleAnchors = (nodeObj, gridGapPx) => {
  if (!nodeObj || !Array.isArray(nodeObj?.data?.handles) || nodeObj.data.handles.length === 0) {
    return null;
  }
  const handles = nodeObj.data.handles;
  const x = (nodeObj.positionAbsolute?.x ?? nodeObj.position?.x ?? 0);
  const y = (nodeObj.positionAbsolute?.y ?? nodeObj.position?.y ?? 0);
  const w = Number(nodeObj.width) || 0;
  const h = Number(nodeObj.height) || 0;
  const ang = typeof nodeObj?.data?.angle === 'number' ? nodeObj.data.angle : VERTICAL_ORIENTATION;
  const mapPos = (pos) => {
    if (ang === VERTICAL_ORIENTATION) return pos;
    switch (pos) {
      case Position.Top: return Position.Left;
      case Position.Bottom: return Position.Right;
      case Position.Left: return Position.Top;
      case Position.Right: return Position.Bottom;
      default: return pos;
    }
  };
  const isThreeWindingNode = (nodeObj?.data?.baseAlias || nodeObj?.data?.alias) === THREE_WINDING_BASE_ALIAS;
  const sourceHandles = isThreeWindingNode
    ? handles.filter((h) => (h?.type || '').toLowerCase() === 'source')
    : null;
  const isSwitchTransferNode = (nodeObj?.data?.baseAlias || nodeObj?.data?.alias) === SWITCH_TRANSFER_BASE_ALIAS;
  const targetHandles = isSwitchTransferNode
    ? handles.filter((h) => (h?.type || '').toLowerCase() === 'target')
    : null;
  const switchTransferSourceHandles = isSwitchTransferNode
    ? handles.filter((h) => (h?.type || '').toLowerCase() === 'source')
    : null;
  const results = [];

  handles.forEach((handleCfg, idx) => {
    if (!handleCfg) return;
    const role = (handleCfg.type || '').toLowerCase();
    if (role !== 'source' && role !== 'target') return;
    const mapped = mapPos(handleCfg.position);
    const isHorizontalEdge = mapped === Position.Top || mapped === Position.Bottom;
    const size = isHorizontalEdge ? w : h;
    const origin = isHorizontalEdge ? x : y;

    let offsetPx = null;

    if (
      offsetPx === null
      && isThreeWindingNode
      && role === 'source'
      && Array.isArray(sourceHandles)
      && sourceHandles.length >= 2
    ) {
      const srcIdx = sourceHandles.indexOf(handleCfg);
      if (srcIdx !== -1) {
        const fraction = Number(resolveThreeWindingHandleFraction(srcIdx, sourceHandles.length));
        if (Number.isFinite(fraction)) {
          offsetPx = clamp(fraction * size, 0, size);
        }
      }
    }

    if (
      offsetPx === null
      && isSwitchTransferNode
      && role === 'target'
      && Array.isArray(targetHandles)
      && targetHandles.length >= 2
    ) {
      const tgtIdx = targetHandles.indexOf(handleCfg);
      if (tgtIdx !== -1) {
        const fraction = Number(resolveSwitchTransferHandleFraction('target', tgtIdx, targetHandles.length));
        if (Number.isFinite(fraction)) {
          offsetPx = clamp(fraction * size, 0, size);
        }
      }
    }

    if (
      offsetPx === null
      && isSwitchTransferNode
      && role === 'source'
      && Array.isArray(switchTransferSourceHandles)
      && switchTransferSourceHandles.length > 0
    ) {
      const srcIdx = switchTransferSourceHandles.indexOf(handleCfg);
      if (srcIdx !== -1) {
        const fraction = Number(resolveSwitchTransferHandleFraction('source', srcIdx, switchTransferSourceHandles.length));
        if (Number.isFinite(fraction)) {
          offsetPx = clamp(fraction * size, 0, size);
        }
      }
    }

    if (offsetPx === null) {
      offsetPx = resolveGridAlignOffset({
        gridAlign: handleCfg.gridAlign,
        indexFallback: idx,
        size,
        origin,
        spacing: gridGapPx,
      });
    }

    if (offsetPx === null) {
      const style = handleCfg.style || {};
      const raw = isHorizontalEdge ? (style.left ?? style.right) : (style.top ?? style.bottom);
      offsetPx = parseOffsetPx(raw, size);
    }

    if (offsetPx === null) {
      offsetPx = size > 0 ? size / 2 : 0;
    }

    let hx = x;
    let hy = y;
    if (isHorizontalEdge) {
      hx = x + offsetPx;
      hy = mapped === Position.Top ? y : y + h;
    } else {
      hy = y + offsetPx;
      hx = mapped === Position.Left ? x : x + w;
    }

    results.push({
      role,
      id: handleCfg.id || `${idx}`,
      x: hx,
      y: hy,
    });
  });

  return results;
};
